import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { EventService, GlobalSpinnerMessages } from '../../services/event.service';

@Component({
    selector: 'app-global-spinner',
    templateUrl: './global-spinner.component.html',
    styleUrls: ['./global-spinner.component.css'],
    standalone: false
})
export class GlobalSpinnerComponent implements OnInit, OnDestroy {
  private uniqueID: string;
  private _displayModal: boolean;
  private _currentMsg: string;
  private _showCloseButton: boolean;
  private _progressCurrent: number;
  private _progressTotal: number;
  private _percentComplete: number;
  private _showProgress: boolean;
  private readonly timeoutLength: number = 10000;
  private subscriptions: Subscription[] = [];

  // properties
  public get displayModal(): boolean {
    return this._displayModal;
  }
  public get currentMsg(): string {
    return this._currentMsg;
  }
  public get showCloseButton(): boolean {
    return this._showCloseButton;
  }

  public get progressCurrent(): number {
    return this._progressCurrent;
  }

  public get progressTotal(): number {
    return this._progressTotal;
  }

  public get percentComplete(): number {
    return this._percentComplete;
  }

  public get showProgress(): boolean {
    return this._showProgress;
  }

  public get divDisplay(): string {
    return this._displayModal ? 'block' : 'none';
  }

  public get spinnerModalLeftEdge(): number {
    return (window.innerWidth - 200) / 2;
  }

  public get spinnerModalTopEdge(): number {
    return window.scrollY + window.outerHeight * 0.3;
  }

  constructor(private eventService: EventService) {}

  public ngOnInit(): void {
    // listen for local openSpinner events to show spinner
    this.subscriptions.push(
      this.eventService.onOpenSpinnerReceived((msg: string, showProgress: boolean, uniqueID: string) => {
        this.showSpinner(msg, showProgress, uniqueID);
      })
    );

    // listen for local updateSpinnerMessage events to update spinner message
    this.subscriptions.push(
      this.eventService.onUpdateSpinnerMsg((msg: string, uniqueID: string) => {
        this.updateSpinnerMsg(msg, uniqueID);
      })
    );

    // listen for signalr progress count events
    this.subscriptions.push(
      this.eventService.onSendProgressCountReceived((current: number, total: number, uniqueID: string) => {
        this.updateProgress(current, total, uniqueID);
      })
    );

    // listen for local closeSpinner events to close spinner
    this.subscriptions.push(
      this.eventService.onCloseSpinnerReceived((downloadID: string) => {
        this.hideSpinner(downloadID);
      })
    );

    // listen for signalr download complete events
    this.subscriptions.push(
      this.eventService.onDownloadCompleteReceived((downloadID: string) => {
        this.hideSpinner(downloadID);
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub?.unsubscribe());
  }

  public showSpinner(msg: string, showProgress: boolean, downloadID: string): void {
    // default if no message is provided
    this._currentMsg = msg ?? GlobalSpinnerMessages.loading;
    this.uniqueID = downloadID.toLowerCase();
    this._showCloseButton = false;
    this._percentComplete = 0;
    this._progressCurrent = 0;
    this._progressTotal = 0;
    this._showProgress = showProgress;
    this._displayModal = true;

    // show close button after (timeoutLength) ms if not already closed
    setTimeout(() => {
      this._showCloseButton = true;
    }, this.timeoutLength);
  }

  public updateProgress(current: number, total: number, uniqueID: string): void {
    if (this.matchUniqueID(uniqueID)) {
      this._progressCurrent = current;
      this._progressTotal = total;
      this._percentComplete = (current / total) * 100;
    }
  }

  public updateSpinnerMsg(msg: string, uniqueID: string): void {
    if (this.matchUniqueID(uniqueID) && msg) {
      this._currentMsg = msg;
    }
  }

  public hideSpinner(uniqueID: string, force = false): void {
    if (force || this.matchUniqueID(uniqueID)) {
      this._displayModal = false;
    }
  }

  public matchUniqueID(uniqueID: string): boolean {
    return this.uniqueID && uniqueID.trim().toLowerCase() === this.uniqueID.trim().toLowerCase();
  }
}
